import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { CompanionExpression } from '@/lib/companion';
import expressions from '@/assets/companion/xiaoshao-expressions.webp';
import compactSheet from '@/assets/companion/xiaoshao-nav.png';
import './companion.css';

const FRAMES: Record<CompanionExpression, number> = { idle: 0, blink: 1, left: 2, right: 3, wave: 4, notice: 5 };
const IDLE: { expression: CompanionExpression; duration: number }[] = [
  { expression: 'idle', duration: 6800 }, { expression: 'blink', duration: 180 },
  { expression: 'idle', duration: 8400 }, { expression: 'left', duration: 1000 },
  { expression: 'idle', duration: 7200 }, { expression: 'blink', duration: 180 },
  { expression: 'idle', duration: 9000 }, { expression: 'right', duration: 1000 },
];

export function XiaoShao({ expression, motion = true, paused = false, reaction = 0, compact = false, className = '' }: {
  expression?: CompanionExpression; motion?: boolean; paused?: boolean; reaction?: number; compact?: boolean; className?: string;
}) {
  const [idle, setIdle] = useState<CompanionExpression>('idle');
  const [waving, setWaving] = useState(false);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [reduced, setReduced] = useState(() => matchMedia('(prefers-reduced-motion: reduce)').matches);
  const active = motion && !reduced && visible;

  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReduced(media.matches);
    const updateVisibility = () => setVisible(!document.hidden);
    media.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      media.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    if (!active || paused || (expression && expression !== 'notice')) return;
    let index = 0;
    let timer: number;
    const tick = () => {
      setIdle(IDLE[index].expression);
      timer = window.setTimeout(() => { index = (index + 1) % IDLE.length; tick(); }, IDLE[index].duration);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [active, expression, paused]);

  useEffect(() => {
    if (!reaction || !active) return;
    setWaving(true);
    const timer = window.setTimeout(() => setWaving(false), 1500);
    return () => { window.clearTimeout(timer); setWaving(false); };
  }, [reaction, active]);

  const state = expression && expression !== 'notice' ? expression
    : active && waving ? 'wave' : active && !paused && idle !== 'idle' ? idle : expression ?? 'idle';
  const frame = FRAMES[state];
  const col = frame % 3;
  const row = Math.floor(frame / 3);
  // 導覽列的 32px 版本：整張小拼圖用 img 畫出來，再用外框裁出這一格。
  // iPhone Safari 把 1536px 大拼圖當背景縮到 32px 時，畫出來會往右偏、被切掉半邊（2026-10-30 實機截圖）。
  if (compact) {
    return (
      <span aria-hidden="true" data-expression={state} className={`xiaoshao xiaoshao-compact ${className}`}
        style={{ '--xs-col': col, '--xs-row': row } as CSSProperties}>
        <img src={compactSheet} alt="" width={288} height={192} draggable={false} />
      </span>
    );
  }
  const style = {
    backgroundImage: `url(${expressions})`,
    backgroundPosition: `${col * 50}% ${row * 100}%`,
  } as CSSProperties;
  return <span aria-hidden="true" data-expression={state} className={`xiaoshao ${className}`} style={style} />;
}
